import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Package, Calendar, Sparkles, Receipt, ChevronRight } from "lucide-react";

/**
 * ServicesBoughtSection — everything the user has bought / booked / done.
 * Grouped by care packages, consult bookings (upcoming + past), and
 * completed adjunctive therapy sessions.
 */

const TAB_META = {
  packages:  { icon: Package,   label: "Care packages" },
  upcoming:  { icon: Calendar,  label: "Upcoming consults" },
  past:      { icon: Calendar,  label: "Past consults" },
  therapies: { icon: Sparkles,  label: "Therapy sessions" },
};

function EmptyRow({ text, to, ctaLabel }) {
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-6 text-center text-[color:var(--mano-ink-soft)]">
      {text}
      {to && (
        <Link to={to} className="text-[color:var(--mano-primary)] font-semibold ml-1">
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}

export default function ServicesBoughtSection() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("packages");

  useEffect(() => {
    api.get("/me/services").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const counts = {
    packages: data.care_package_count,
    upcoming: data.upcoming_bookings.length,
    past: data.past_bookings.length,
    therapies: data.therapy_completed_count,
  };

  return (
    <section data-testid="services-bought" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="uppercase-label inline-flex items-center gap-1">
            <Receipt size={13} /> Your services
          </div>
          <div className="text-xs text-[color:var(--mano-ink-soft)] mt-1">
            <b>{data.total_services}</b> total items across care packages, consults, and therapies.
          </div>
        </div>
        <div className="inline-flex rounded-full border border-[color:var(--mano-border)] bg-white p-1 text-xs flex-wrap">
          {Object.entries(TAB_META).map(([k, m]) => {
            const Icon = m.icon;
            const on = tab === k;
            return (
              <button
                key={k}
                data-testid={`services-tab-${k}`}
                onClick={() => setTab(k)}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-semibold transition-colors ${
                  on ? "bg-[color:var(--mano-primary)] text-white" : "text-[color:var(--mano-ink-soft)]"
                }`}
              >
                <Icon size={11} />
                {m.label}
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    on ? "bg-white/25" : "bg-[color:var(--mano-border)]/50"
                  }`}
                >
                  {counts[k]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        {tab === "packages" && (
          data.care_packages.length === 0
            ? <EmptyRow text="No care packages yet." to="/therapies" ctaLabel="Browse care packages →" />
            : data.care_packages.map((p) => (
                <div key={p.id} data-testid={`services-package-${p.id}`} className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{p.package_name}</div>
                      <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">
                        Purchased {new Date(p.started_at).toLocaleDateString()}
                        {p.amount_paise != null && <> · ₹{(p.amount_paise/100).toFixed(0)}</>}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      Active
                    </span>
                  </div>
                  {p.components?.length > 0 && (
                    <ul className="mt-3 text-sm text-[color:var(--mano-ink-soft)] space-y-0.5">
                      {p.components.map((c, i) => <li key={i}>• {c.label}</li>)}
                    </ul>
                  )}
                </div>
              ))
        )}

        {tab === "upcoming" && (
          data.upcoming_bookings.length === 0
            ? <EmptyRow text="No upcoming consults." to="/experts" ctaLabel="Book a specialist →" />
            : data.upcoming_bookings.map((b) => (
                <BookingRow key={b.id} b={b} testid={`services-upcoming-${b.id}`} />
              ))
        )}

        {tab === "past" && (
          data.past_bookings.length === 0
            ? <EmptyRow text="No past consults yet." />
            : data.past_bookings.slice(0, 25).map((b) => (
                <BookingRow key={b.id} b={b} testid={`services-past-${b.id}`} past />
              ))
        )}

        {tab === "therapies" && (
          data.therapy_sessions.length === 0
            ? <EmptyRow text="No adjunctive therapy sessions completed yet." to="/therapies" ctaLabel="Explore therapies →" />
            : data.therapy_sessions.slice(0, 25).map((e, i) => (
                <div key={i} data-testid={`services-therapy-${i}`} className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{e.therapy_title || e.therapy_id?.slice(0, 8)}</div>
                    <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">
                      Completed {new Date(e.completed_at).toLocaleString()}
                      {e.rating != null && <> · {e.rating}★</>}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    Done
                  </span>
                </div>
              ))
        )}
      </div>
    </section>
  );
}

function BookingRow({ b, testid, past }) {
  return (
    <Link
      to={`/session/${b.id}`}
      data-testid={testid}
      className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-4 flex items-center justify-between gap-3 flex-wrap hover:border-[color:var(--mano-primary)] transition-colors"
    >
      <div className="min-w-0">
        <div className="font-semibold truncate">{b.expert_name}</div>
        <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">
          {new Date(b.start_iso).toLocaleString()}
          {b.mode && <> · {b.mode.replace("_", " ")}</>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {past && b.recording_available && (
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)]">
            Recording
          </span>
        )}
        <ChevronRight size={16} className="text-[color:var(--mano-ink-soft)]" />
      </div>
    </Link>
  );
}
